import { Button, Dropdown, Icon, Space, ThemeSwitch, Navbar as UINavbar, BREAKPOINTS, DropdownItem, NavbarItem, BottomNavigation, BottomNavigationItem } from "@itb/ui";
import { useAuth } from "src/hooks/useAuth";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import Avatar from "../Avatar";
import Search from "../Search";
import { useState } from "react";
import RiskRadarItem from "./RiskRadarItem";
import ThemeNavbarItemMobile from "./ThemeNavbarItemMobile";

const Tools = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const UserAvatar = styled(Avatar)`
  min-width: 32px;
`;

const UserName = styled.div`
  font-size: 13px;
  font-weight: 500;
  margin-left: 10px;
`;
const TenantName = styled.div`
  font-size: 13px;
  margin-left: 4px;
  color: ${props => props.theme.colors.darkgray};
  white-space: nowrap;
`;

const UserDropdown = styled(Dropdown)`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
`;

const UserDropdownContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Desktop = styled(Space)`
  display: flex;
  padding-left: 10px;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    display: none;
  }
`;

const Mobile = styled(Space)`
  display: none;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    display: flex;
    align-items: center;
  }
`;

const ThemeSwitchItem = styled(ThemeSwitch)`
  width: 100%;
  background: none;
  border: none;
`;

const SearchButton = styled(Icon)`
  cursor: pointer;
  display: none;

  @media(max-width: ${BREAKPOINTS.tablet}) {
    display: block;
  }
  @media(max-width: ${BREAKPOINTS.mobile}) {
    display: block;
    margin-right: 10px;
  }
`;

const MenuIcon = styled(Icon)`
  margin-top: 4px;
`

const StyledTenantsNavbarItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 16px;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  box-sizing: border-box;
  line-height: 22px;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;



const Navbar: React.FC = () => {
  const { user, tenant, tenants, setCurrentTenantId, signOut } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchOpenForMobile, setSearchOpenForMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const signIn = () => navigate("/signin", { state: { redirectUri: `${location.pathname}${location.search}` } });

  const items: NavbarItem[] = [
    { text: "Home", path: "/", active: location.pathname === "/" },
    { text: "Positions", path: "/positions", active: location.pathname.startsWith("/positions") },
    { text: "Invoices", path: "/invoices", active: location.pathname.startsWith("/invoices") },
    { text: "Discover", path: "/discover", active: location.pathname.startsWith("/discover") },
    { text: "Risk Radar", wrapper: RiskRadarItem, active: location.pathname.startsWith("/risk-radar") },
    { text: "Theme", wrapper: ThemeNavbarItemMobile as any, mobile: true },
  ];

  if (tenant?.staking_config) items.push({ text: "Staking", path: "/staking", active: location.pathname.startsWith("/staking") });

  if (user) {
    if (tenants) {
      const TenantsWrapper: React.FC = () => {
        const [open, setOpen] = useState(false);
        const sortedTenants = [...tenants].sort((a, b) => a.name.localeCompare(b.name));
        if (tenants.length == 1) {
          return (
            <StyledTenantsNavbarItem>
              <span>{user.username} <span style={{ color: 'darkgray' }}>({tenant?.name})</span></span>
            </StyledTenantsNavbarItem>
          );
        }
        return (
          <div>
            <StyledTenantsNavbarItem onClick={() => setOpen(!open)}>
              <span>{user.username} <span style={{ color: 'darkgray' }}>({tenant?.name})</span></span>
              <Icon icon={open ? "expand_less" : "expand_more"} />
            </StyledTenantsNavbarItem>
            {open && (
              <div style={{ maxHeight: "302px", overflow: "auto" }}>
                {sortedTenants.map(tenant => (
                  <StyledTenantsNavbarItem
                    key={tenant.tenant_id}
                    onClick={() => {
                      setCurrentTenantId(tenant.tenant_id);
                      setOpen(false);
                    }}
                  >
                    <span>{tenant.name}</span>
                  </StyledTenantsNavbarItem>
                ))}
              </div>
            )}
          </div>
        );
      };
      items.push({ text: "Tenants", wrapper: TenantsWrapper as any, mobile: true });
    }
    
    const SignOutWrapper: React.FC = () => (
      <StyledTenantsNavbarItem onClick={signOut}>
        <div style={{display: 'flex', alignItems: 'center'}}>
          <span>Sign out</span>
          <Icon icon="logout" style={{marginLeft: '8px'}} />
        </div>
      </StyledTenantsNavbarItem>
    );
    items.push({ text: "Sign out", wrapper: SignOutWrapper as any, mobile: true });
  }

  const BottomNavigationItems: BottomNavigationItem[] = [
    { text: "Portfolio Overview", mobileText: "Overview", icon: "home", path: "/" },
    { text: "Positions", icon: "analytics", path: "/positions", active: location.pathname.startsWith("/positions") },
    { text: "Invoices", icon: "receipt_long", path: "/invoices" },
    { text: "Discover", icon: "lightbulb", path: "/discover", active: location.pathname.startsWith("/discover") },
    { text: "Risk Radar", icon: "risk_radar", wrapper: RiskRadarItem },
  ];

  if (tenant?.staking_config) BottomNavigationItems.push({ text: "Staking", icon: "ethereum", path: "/staking" });

  const signedInItems: DropdownItem[] = [
    ...(tenants && tenants.length > 1 ? [
      {
        key: "tenant",
        label: "Tenants",
        icon: "group",
        popupStyle: { overflow: "auto", maxHeight: "302px" },
        popupOffset: [-410, 0],   // Fixes flickering
        children: tenants?.map(tenant => ({
          key: tenant.tenant_id,
          label: tenant.name,
          onClick: () => setCurrentTenantId(tenant.tenant_id),
        })).sort((a, b) => a.label.localeCompare(b.label)),
      } as DropdownItem,
      { key: "divider", type: "divider" } as DropdownItem,
    ] : []),
    { key: "signout", label: "Sign out", icon: "logout", onClick: signOut },
  ];

  const anonymousItems: DropdownItem[] = [{ key: "signin", label: "Sign In", icon: "login", onClick: signIn }];

  const tools = (
    <Tools>
      <Search
      open={searchOpen}
      setOpen={setSearchOpen}
      />
      <SearchButton icon={searchOpen ? "close" : "search"} onClick={() => setSearchOpen(p => !p)} />
      <Desktop size={20}>
        <ThemeSwitch />
        {user ? (
          <UserDropdown items={signedInItems}>
            <UserDropdownContent>
              <UserAvatar />
              <UserName>{user.username}</UserName>
              <TenantName>({tenant?.name})</TenantName>
              <Icon icon="expand_more" />
            </UserDropdownContent>
          </UserDropdown>
        ) : (
          <Button onClick={signIn}>Sign In</Button>
        )}
      </Desktop>
      <Mobile>
        <Dropdown
          items={[
            { key: "theme-switch", label: <ThemeSwitchItem /> },
            ...(user ? signedInItems : anonymousItems)
          ]}
        >
          <MenuIcon icon="menu" />
        </Dropdown>
      </Mobile>
    </Tools>
  );

  const mobileNavTools = (
    <>
      <Search
      open={searchOpenForMobile}
      setOpen={setSearchOpenForMobile}
      />
      <SearchButton icon={searchOpenForMobile ? "close" : "search"} onClick={() => setSearchOpenForMobile(p => !p)} />
    </>
  );

  return (
    <>
      <UINavbar items={items} tools={tools} mobileNavTools={mobileNavTools} />
      <BottomNavigation items={BottomNavigationItems} />
    </>
  )
};

export default Navbar;
